<template>
  <div ref="mainContainer">
    <div class="mx-auto max-w-7xl px-3 !pt-5 pb-5 grid gap-4 grid-cols-1">
      <JoszakiLoading :active="isLoadingCity" is-full-page />
      <BreadCrumbs :links="getBreadCrumbs" />
      <CitySelector class="block md:hidden" @select="setCity($event)" />
      <div class="flex flex-col w-full md:flex-row">
        <HeroHeader
          :hero-title="heroTitle"
          :hero-image="getHeroImageUrl"
          :hero-description="heroDescription"
          class="w-full"
        >
          <FavouriteButton
            v-if="professionTask"
            :entity-type="FAVOURITE_ENTITY_TYPES.PROFESSION_TASK"
            :entity-id="professionTask.id"
            wrapper-class="absolute top-4 right-4 z-10"
            button-class="w-10 h-10 rounded-full bg-white/90 shadow-sm text-xl"
          />
        </HeroHeader>
      </div>

      <div class="flex flex-col md:flex-row gap-2">
        <BlockTender
          class="flex-1"
          instance-id="banner-block"
          text-input
          @requestTender="
            onRequestTender({ source: 'banner-block', description: $event })
          "
        />
      </div>

      <div
        v-if="!city && professionTask?.contentHtml"
        class="flex flex-col gap-6"
      >
        <div class="grid grid-cols-1 gap-6 lg:grid-cols-2">
          <CardPerson
            v-for="(person, index) in topPersonList"
            :key="index"
            class="h-full"
            :highlighted-city="city"
            :highlighted-profession-task="professionTask"
            :person="person"
            :show-go-to-house-km="!city"
            :online="isOnlineProfessionTask"
            show-active-indicator
            @callProfessional="onCallProfessional(person)"
            @select="onClickPersonCard(person)"
            @requestTender="
              ({ profession }) =>
                onRequestTender({ person, source: 'personCard', profession })
            "
          />
        </div>
        <JoszakiButton @click="showMoreClick">
          {{ $t("pricesPage.showMoreProfessionals") }}
        </JoszakiButton>
      </div>

      <SuggestionContent
        v-if="!city && professionTask?.contentHtml"
        id="tartalom"
        :profession-task="professionTask"
        :price-ranges="totalPriceRanges"
        @requestTender="
          onRequestTender({ source: 'suggestionContent', description: $event })
        "
        @selectCity="setCity($event)"
      />

      <div class="grid w-full gap-4">
        <JoszakiHeading id="szakemberek" ref="szakemberek" :level="2">
          {{ $t("professionTaskList.persons") }}
        </JoszakiHeading>

        <NuxtLink
          v-if="professionTaskPageSlug"
          class="text-primary hover:underline"
          :to="
            localePath({
              name: 'professionPage',
              params: { profession: professionTaskPageSlug },
            })
          "
        >
          {{
            $t("pricesPage.professionTaskPageLink", {
              task: lowerFirst(professionTask.name),
            })
          }}
        </NuxtLink>

        <OnlineCoverageBanner
          v-if="isOnlineProfessionTask"
          class="sticky top-20 z-30"
        />

        <div
          v-if="!city"
          class="grid grid-cols-1 gap-8"
          :class="{
            'lg:grid-cols-2': professionTask?.contentHtml && !city,
          }"
        >
          <BlockCitySelector
            v-if="!city && found"
            :value="city"
            @select="setCity($event)"
          />
          <BlockTender
            v-if="!city && professionTask?.contentHtml"
            instance-id="cards-block"
            :text-input="true"
            @requestTender="
              onRequestTender({ source: 'cards-block', description: $event })
            "
          />
        </div>

        <NotFoundProfessionTaskList
          v-if="!found"
          class="w-full p-3"
          :profession="profession"
          :city="city"
        />

        <div class="grid gap-4">
          <div ref="personGrid" class="grid grid-cols-1 gap-6 lg:grid-cols-2">
            <BlockCitySelector
              v-if="!city && found"
              :value="city"
              class="sticky z-30 lg:col-span-2 top-3"
              :class="{
                hidden: !showStickyCitySelector,
              }"
              @select="setCity($event)"
            />
            <template v-if="isLoadingProfessionals">
              <CardPersonPlaceholder v-for="i in pageSize" :key="i" />
            </template>
            <template v-else>
              <CardPerson
                v-for="(person, index) in personList"
                :key="index"
                class="h-full"
                :highlighted-city="city"
                :highlighted-profession-task="professionTask"
                :person="person"
                :show-go-to-house-km="!city"
                :online="isOnlineProfessionTask"
                show-active-indicator
                @callProfessional="onCallProfessional(person)"
                @select="onClickPersonCard(person)"
                @requestTender="
                  ({ profession }) =>
                    onRequestTender({
                      person,
                      source: 'personCard',
                      profession,
                    })
                "
              />
            </template>
          </div>
        </div>

        <PaginationComponent :pagination-info="paginationInfo" />
      </div>

      <BackToPersonList
        v-if="profession && found"
        class="mx-auto my-12"
        :text="$t('personList.cantFindDescription')"
        :profession-name="profession?.name ?? ''"
        :back-url="personListUrl"
      />

      <div class="block mb-12">
        <JoszakiHeading class="text-center" :level="3">
          {{
            $t("professionTaskList.quickOfferText", {
              professionName: profession?.name ?? "",
            })
          }}
        </JoszakiHeading>
        <QuickOffer
          @requestTender="
            onRequestTender({ source: 'quickOffer', description: $event })
          "
        />
      </div>
    </div>

    <div
      v-if="
        !city && professionTask?.contentHtml && stickyNavButtonContainerWidth
      "
      class="fixed bottom-0 left-0 z-40"
      :style="{ width: `${stickyNavButtonContainerWidth}px` }"
    >
      <div
        class="flex flex-row items-stretch gap-5 bg-white p-3 border border-solid border-t-gray-300 max-xl:left-0 max-xl:right-0 xl:w-[1256px] xl:mx-auto xl:rounded-t-md"
      >
        <JoszakiButton
          class="lg:!px-16 text-sm"
          expanded
          :outline="!isPriceGuideNavButtonActive"
          :inverted="!isPriceGuideNavButtonActive"
          href="#tartalom"
          @click="priceGuideNavButtonClick"
        >
          {{ $t("pricesPage.priceGuide") }}
        </JoszakiButton>
        <JoszakiButton
          class="lg:!px-16 text-sm"
          expanded
          :outline="isPriceGuideNavButtonActive"
          :inverted="isPriceGuideNavButtonActive"
          href="#szakemberek"
          @click="professionalsNavButtonClick"
        >
          {{ $t("pricesPage.professionalsAndPrices") }}
        </JoszakiButton>
      </div>
    </div>
  </div>
</template>

<router>
{
name: "prices"
}
</router>

<script>
import { mapGetters } from "vuex";

import QuickOffer from "~/components/_refactored/app/boxes/QuickOffer.vue";
import BlockCitySelector from "~/components/_refactored/blocks/BlockCitySelector.vue";
import BlockTender from "~/components/_refactored/blocks/BlockTender.vue";
import OnlineCoverageBanner from "~/components/_refactored/blocks/OnlineCoverageBanner.vue";
import BreadCrumbs from "~/components/_refactored/BreadCrumbs.vue";
import CardPersonPlaceholder from "~/components/_refactored/card/CardPersonPlaceholder.vue";
import JoszakiButton from "~/components/_refactored/common/JoszakiButton.vue";
import PaginationComponent from "~/components/_refactored/common/PaginationComponent.vue";
import SuggestionContent from "~/components/_refactored/layout/SuggestionContent.vue";
import CitySelector from "~/components/_refactored/pricesPage/CitySelector.vue";
import { useFetchProfessionals } from "~/components/_refactored/profession";
import HeroHeader from "~/components/HeroHeader.vue";
import BackToPersonList from "~/components/people/BackToPersonList.vue";
import CardPerson from "~/components/people/CardPerson.vue";
import NotFoundProfessionTaskList from "~/components/people/NotFoundProfessionTaskList.vue";
import { permanentRedirect, redirectHomeOnMissing } from "~/helpers/redirect";
import { useRequestTender } from "~/composables/requestTender";
import { useNoBrandActive } from "~/composables/useNoBrandActive";
import { EVENT_PAGE_NAMES } from "~/data/pageNames";
import CITY_BY_SEO_NAME from "~/graphql/cityBySeoName";
import PRICES_SSR from "~/graphql/priceListSSR.graphql";
import PROFESSION_TASK_PAGE_LINK from "~/components/_refactored/professionTaskPage/professionTaskPageLink.graphql";
import { lowerFirst } from "~/components/_refactored/professionTaskPage/professionTaskCityContent";
import { hashParamsMixin } from "~/composables/useHashParams";
import FavouriteButton from "~/components/_refactored/favourite/FavouriteButton.vue";
import { FAVOURITE_ENTITY_TYPES } from "~/composables/favourite/favouriteEntityTypes";
import { isOnlineProfessionEnabled } from "~/experiments/onlineProfession";
import { useProfessionNavLinksStore } from "~/stores/professionNavLinks";

export default {
  name: "ProfessionTaskListPage",
  components: {
    CardPersonPlaceholder,
    CardPerson,
    NotFoundProfessionTaskList,
    BackToPersonList,
    QuickOffer,
    HeroHeader,
    BreadCrumbs,
    SuggestionContent,
    JoszakiButton,
    BlockTender,
    BlockCitySelector,
    OnlineCoverageBanner,
    PaginationComponent,
    CitySelector,
    FavouriteButton,
  },
  mixins: [hashParamsMixin],
  layout: "DefaultLayout",
  setup() {
    const fetchProfessionals = useFetchProfessionals();
    return {
      noBrandActive: useNoBrandActive(),
      ...useRequestTender(),
      fetchProfessionals,
    };
  },
  async asyncData(context) {
    // Note: Hash-based params - server always loads page 1 without city
    // Client-side mounted() will load the correct page/city based on hash
    // 301 redirects for ?page=X and ?city=X are handled by server middleware

    const start = +new Date();
    const citySeoName = null; // City comes from hash (client-side only)
    const professionTaskSeoName = context.route.params.professionTask;

    const pageSize = context.$device.isMobile ? 20 : 50;
    const fetchProfessionals = useFetchProfessionals(context);

    try {
      const page = 1; // Always fetch page 1 on SSR (hash not visible to server)
      const path = context.route.path;

      const [response, professionTaskPageLink] = await Promise.all([
        context.$query(PRICES_SSR, {
          professionTaskSeoName,
          citySeoName,
          path,
        }),
        context
          .$query(PROFESSION_TASK_PAGE_LINK, { professionTaskSeoName })
          .catch(() => null),
      ]);
      const professionTask = response.professionTaskBySeoName;
      const redirect = response.redirect;
      const city = response.cityBySeoName;
      const peoplePriceTotals = response.peoplePriceTotals;

      if (city) {
        await context.store.dispatch("listPage/setCity", city);
      }
      if (!professionTask) {
        if (redirect) {
          return permanentRedirect({
            res: context.res,
            redirect: context.redirect,
            location: redirect,
          });
        }
        redirectHomeOnMissing(context);
        return;
      }
      await context.store.dispatch(
        "listPage/setProfessionTask",
        professionTask
      );

      const professions = [...professionTask.professions];
      professions.sort((a, b) => b.popularity - a.popularity);
      await Promise.all([
        context.store.dispatch("listPage/setProfession", professions[0]),
        useProfessionNavLinksStore(context.$pinia).load(context, {
          professionSeoName: professions[0]?.seoName,
        }),
      ]);

      const { people: personList, pagination: paginationInfo } =
        await fetchProfessionals({
          professionTaskSeoName,
          citySeoName,
          page,
          pageSize,
        });

      const end = +new Date() - start;

      return {
        professionTask,
        professionTaskPageSlug:
          professionTaskPageLink?.professionTaskBySeoName?.page?.slug ?? null,
        personList,
        paginationInfo,
        totals: peoplePriceTotals,
        city,
        end,
        pageSize,
        isPriceGuideNavButtonActive: context.route.hash !== "#szakemberek",
      };
    } catch (error) {
      throw new Error(error);
    }
  },
  data() {
    return {
      professionTaskPageSlug: null,
      isLoadingProfessionals: false,
      isLoadingCity: false,
      showStickyCitySelector: false,
      stickyNavButtonContainerWidth:
        this.$refs?.mainContainer?.clientWidth ?? null,
      FAVOURITE_ENTITY_TYPES,
    };
  },
  head() {
    return {
      title: this.$helpers.capitalize(
        this.$t("professionTaskList.title", {
          name: this.professionTask.name,
          year: new Date().getFullYear(),
          domain: this.$t("branding.webpage"),
        })
      ),
      meta: [
        {
          hid: "og:title",
          property: "og:title",
          content: this.$helpers.capitalize(
            this.$t("professionTaskList.title", {
              name: this.professionTask.name,
              year: new Date().getFullYear(),
              domain: this.$t("branding.webpage"),
            })
          ),
        },
        {
          hid: "og:url",
          property: "og:url",
          content: `${this.$helpers.topDomain}${this.$route.fullPath}`,
        },
        {
          hid: "og:image",
          property: "og:image",
          content: this.getHeroImageUrl,
        },
        {
          hid: "description",
          name: "description",
          content: this.professionTask.description,
        },
        {
          hid: "og:description",
          property: "og:description",
          content: this.professionTask.description,
        },
      ],
      link: [
        {
          rel: "canonical",
          href: `${this.$helpers.topDomain}${this.$route.path}`,
        },
      ],
    };
  },
  computed: {
    ...mapGetters("listPage", {
      profession: "profession",
    }),
    topPersonList() {
      return this.personList.slice(0, 4);
    },
    isOnlineProfessionTask() {
      if (!isOnlineProfessionEnabled(this.$ab)) {
        return false;
      }
      const professions = this.professionTask?.professions ?? [];
      return professions.length > 0 && professions.every((p) => p.online);
    },
    isMobile() {
      return this.$mq === "mobile";
    },
    heroTitle() {
      return this.city
        ? this.$t("professionTaskList.mainTitleWithCity", {
            seoName: this.professionTask.name,
            city: this.city.name,
            year: new Date().getFullYear(),
          })
        : this.$t("professionTaskList.mainTitle", {
            seoName: this.professionTask.name,
            year: new Date().getFullYear(),
          });
    },
    heroDescription() {
      return this.professionTask.descriptionMarkdown;
    },
    getBreadCrumbs() {
      const links = [
        // {
        //   name: 'Árak',
        //   url: ''
        // }
      ];

      if (this.professionTask.professions.length > 0) {
        const sortedProfessions = [...this.professionTask.professions].sort(
          (a, b) => a.name.localeCompare(b.name)
        );

        const addedProfessionGroups = [];
        sortedProfessions.forEach((p) => {
          if (!addedProfessionGroups.includes(p.professionGroup.seoName)) {
            const professionsPagePath = this.localePath({
              name: "professionsLandingPage",
              hash: `#${p.professionGroup.seoName}`,
            });
            links.push({
              name: p.professionGroup.name,
              page: professionsPagePath,
            });
            addedProfessionGroups.push(p.professionGroup.seoName);
          }
        });

        sortedProfessions.forEach((p) => {
          const path = this.city
            ? this.localePath({
                name: "professionals",
                params: { city: this.city.seoName, profession: p.seoName },
              })
            : this.localePath({
                name: "professionPage",
                params: { profession: p.seoName },
              });
          links.push({
            name: p.name,
            page: path,
          });
        });
      }

      if (this.professionTask) {
        links.push({
          name: this.professionTask.name,
          page: this.city
            ? this.localePath({
                name: "prices",
                params: { professionTask: this.professionTask.seoName },
              })
            : "",
        });
      }

      if (this.city && this.city.name) {
        links.push({
          name: this.city.name,
          url: "",
        });
      }

      return links;
    },
    getHeroImageUrl() {
      return `${process.env.GOOGLE_STORAGE_URL}/${process.env.GOOGLE_STORAGE_ASSETS_BUCKET}/profession_task/${this.professionTask.seoName}.jpg`;
    },
    peopleIds() {
      if (!this.personList) {
        return [];
      }
      return this.personList.map((x) => {
        return x.id;
      });
    },
    found() {
      return this.personList && this.personList.length > 0;
    },
    personListUrl() {
      if (this.city && this.profession) {
        return this.localePath({
          name: "professionals",
          params: {
            profession: this.profession?.seoName,
            city: this.city?.seoName,
          },
        });
      }

      if (this.profession) {
        return this.localePath({
          name: "professionPage",
          params: {
            profession: this.profession?.seoName,
          },
        });
      }

      return this.localePath({
        name: "professionsLandingPage",
      });
    },
    totalPriceRanges() {
      return [
        {
          from: this.totals.minPrice,
          to:
            this.totals.minPrice +
            (this.totals.medianPrice - this.totals.minPrice) / 2,
        },
        {
          from:
            this.totals.minPrice +
            (this.totals.medianPrice - this.totals.minPrice) / 2,
          to:
            this.totals.medianPrice +
            (this.totals.maxPrice - this.totals.medianPrice) / 2,
        },
        {
          from:
            this.totals.medianPrice +
            (this.totals.maxPrice - this.totals.medianPrice) / 2,
          to: this.totals.maxPrice,
        },
      ];
    },
  },
  watch: {
    $route: {
      handler(newVal, oldVal) {
        // Note: City and page changes are handled by hashParamsMixin ($route.hash watcher)
        const hashHasChanged = newVal?.hash !== oldVal?.hash;

        if (hashHasChanged) {
          // Update nav button state based on hash
          const isDataHash = newVal.hash?.includes("=");
          this.isPriceGuideNavButtonActive =
            !newVal.hash?.startsWith("#szakemberek") &&
            (!isDataHash || newVal.hash?.includes("city="));
        }
      },
    },
  },
  beforeDestroy() {
    this.$trackers.pageleave({
      pagename: EVENT_PAGE_NAMES.PRICES_PAGE,
    });
    if (process.client) {
      const mainBody = document.getElementById("main-body");
      mainBody.removeEventListener("scroll", this.handleScroll);
      window.removeEventListener("resize", this.handleWindowResize);
    }
  },
  beforeMount() {
    this.$route.meta.professionGroup =
      this.professionTask?.professions?.[0]?.professionGroup?.seoName;
    this.$route.meta.profession = this.professionTask?.professions[0]?.seoName;
  },
  mounted() {
    this.trackViewItemList();
    this.trackPageView();
    this.seoTrackViewPage();
    // Note: Hash-based pagination is handled by hashParamsMixin via $route.hash watcher

    // Load city from hash on client-side (hash is not available on server)
    const citySeoName = this.hashParams.city;
    if (citySeoName) {
      this.fetchCity(citySeoName).then(() => {
        this.paginate(this.hashPage, this.city?.seoName);
      });
    } else if (this.hashPage > 1) {
      // hashParamsMixin.mounted() handles page-only hash changes
      // but we override onHashParamsChange, so trigger manually
      this.paginate(this.hashPage, this.city?.seoName);
    }

    if (process.client) {
      const mainBody = document.getElementById("main-body");
      mainBody.addEventListener("scroll", this.handleScroll);
      window.addEventListener("resize", this.handleWindowResize);
      this.handleWindowResize();
    }
  },
  methods: {
    lowerFirst,
    /**
     * Called by hashParamsMixin when hash params change.
     * Override to handle city + page hashes, skip navigation hashes like #szakemberek.
     */
    async onHashParamsChange() {
      const hash = this.$route.hash || "";

      // Skip navigation-only hashes (e.g. #szakemberek, #tartalom)
      if (hash && !hash.includes("=")) {
        return;
      }

      const newCitySeoName = this.hashParams.city || null;
      const previousCitySeoName = this.city?.seoName || null;
      const cityChanged = newCitySeoName !== previousCitySeoName;

      if (cityChanged) {
        if (newCitySeoName) {
          await this.fetchCity(newCitySeoName);
          this.trackPageView();
        } else {
          this.city = null;
          await this.$store.dispatch("listPage/setCity", null);
        }
      }

      await this.paginate(this.hashPage, this.city?.seoName);

      if (this.$refs.mainContainer) {
        this.$nextTick(() => {
          this.$refs.mainContainer.scrollIntoView({ behavior: "smooth" });
        });
      }
    },

    /**
     * Called by hashParamsMixin when hash params change (not used directly —
     * onHashParamsChange is overridden to handle city + page together)
     * @param {number} page - Page number from hash
     */
    loadPageData(page) {
      this.paginate(page, this.city?.seoName);
    },

    trackPageView() {
      this.$trackers.pageview({
        pagename: EVENT_PAGE_NAMES.PRICES_PAGE,
        citySeoName: this.city?.seoName,
        professionTaskSeoName: this.professionTask?.seoName,
        professionGroupSeoName:
          this.professionTask?.professions?.[0]?.professionGroup?.seoName,
        professionSeoName: this.professionTask?.professions[0]?.seoName,
        hasContent: !!this.professionTask?.contentHtml,
        hasHeaderDescription: this.heroDescription?.length > 0,
        headerMinPrice: this.totals.minPrice,
      });
    },
    onRequestTender({ person, source, profession = null, description }) {
      this.requestTender({
        person,
        city: this.city,
        profession: profession ?? this.profession,
        source,
        description,
      });
    },
    showMoreClick() {
      this.$refs.szakemberek.$el.scrollIntoView();
      this.$trackers.trackClick("ShowMoreProfessionals");
    },
    handleWindowResize() {
      this.stickyNavButtonContainerWidth = this.$refs.mainContainer.clientWidth;
    },
    handleScroll() {
      if (process.client) {
        const personGridTop = this.$refs.personGrid.getBoundingClientRect().top;
        this.showStickyCitySelector = personGridTop < -20;
      }
    },
    trackViewItemList() {
      if (!process.server) {
        this.$track.customEvent({
          event: "view_item_list",
          city_id: this.city?.id,
          source_page: "nuxt_list_profession_task",
          item_list_name: this.professionTask?.name,
          item_list_id: this.professionTask?.id,
          ecommerce: {
            items: this.personList.map((p) => p.id),
          },
        });
      }
    },
    seoTrackViewPage() {
      this.$track.customEvent({
        event: "pricePageView",
        profession: this.profession?.seoName, // szakma
        profilesWithPrice: this.paginationInfo.total, // Hány szaki van összesen?,
        hasContent: !!this.professionTask?.contentHtml, // van szöveges tartalom? | boolean -> 1 = van 0 = nincs
      });
    },
    onCallProfessional(person) {
      this.trackOpenPersonProfile(person);

      const taskProfessionIds = this.professionTask?.professions?.map(
        (p) => p.id
      );
      const profession = (person?.professions ?? []).find((p) =>
        taskProfessionIds.includes(p.id)
      );

      this.$trackers.trackEvent({
        title: "Clicked On Mate Phone Number",
        data: {
          personId: person.id,
          citySeoName: this.city?.seoName,
          professionTaskSeoName: this.professionTask?.seoName,
          professionGroupSeoName: profession?.professionGroup?.seoName,
          professionSeoName: profession?.seoName,
        },
      });

      // this.$track.customEvent({
      //   event: 'initiate_call',
      //   city_id: this.city.id,
      //   profession_id: this.profession.id,
      //   ecommerce: { items: [person.id] },
      // })

      const path = this.localePath({
        name: "professionalPage",
        params: {
          professionalSeoName: person.seoName,
        },
        query: {
          city: this.city?.seoName,
          professionTask: this.professionTask?.seoName,
        },
      });
      window.open(path);
    },
    onClickPersonCard(person) {
      this.trackOpenPersonProfile(person);

      // this.$trackers.trackEvent({
      //   title: "Clicked On Mate Card",
      //   data: {
      //     personId: person.id,
      //     citySeoName: this.city?.seoName,
      //     professionTaskSeoName: this.professionTask?.seoName,
      //     professionGroupSeoName: profession?.professionGroup?.seoName,
      //     professionSeoName: profession?.seoName,
      //   },
      // });

      const path = this.localePath({
        name: "professionalPage",
        params: {
          professionalSeoName: person.seoName,
        },
        query: {
          city: this.city?.seoName,
          professionTask: this.professionTask?.seoName,
        },
      });
      window.open(path);
    },
    trackOpenPersonProfile(person) {
      this.$track.customEvent({ ecommerce: null });
      this.$track.customEvent({
        event: "select_item",
        city_id: this.city?.id,
        profession_id: this.profession?.id,
        profession_task_id: this.professionTask?.id,
        ecommerce: {
          items: [
            {
              index: this.peopleIds.indexOf(person.id),
              item_id: person.id,
              item_name: person.name,
              item_list_name: `${this.profession?.name} ${this.city?.name}`,
              item_list_id: `${this.city?.seoName}#${this.profession?.seoName}`,
            },
          ],
        },
      });
    },
    setCity(event) {
      if (event) {
        // Set city in hash, reset page to 1
        this.setHashParams({ city: event.seoName, page: 1 });
        this.$joszaki.scrollToTop();
      }
    },
    /**
     * Override hashParamsMixin.setHashParams to support city in hash.
     */
    setHashParams(params, replace = false) {
      if (typeof window === "undefined") return;

      // Merge with current hash params to preserve city when only page changes
      const currentCity =
        params.city !== undefined ? params.city : this.hashParams.city;
      const page = params.page !== undefined ? params.page : this.hashPage;

      const hashParts = [];
      if (currentCity) {
        hashParts.push(`city=${encodeURIComponent(currentCity)}`);
      }
      if (page && page > 1) {
        hashParts.push(`page=${page}`);
      }

      const newHash = hashParts.length ? `#${hashParts.join("&")}` : "";
      const newPath = `${this.$route.path}${newHash}`;

      if (replace) {
        this.$router.replace(newPath);
      } else {
        this.$router.push(newPath);
      }
    },
    /**
     * Override hashParamsMixin.getHashPath to include city in pagination URLs.
     */
    getHashPath(targetPage) {
      const hashParts = [];
      const city = this.hashParams.city;
      if (city) {
        hashParts.push(`city=${encodeURIComponent(city)}`);
      }
      if (targetPage > 1) {
        hashParts.push(`page=${targetPage}`);
      }
      const hashStr = hashParts.length ? `#${hashParts.join("&")}` : "";
      return `${this.$route.path}${hashStr}`;
    },
    getDisplayPrice(person) {
      const price = person.priceByProfessionTask;
      if (!price) {
        return null;
      }

      return {
        ...price,
        professionTask: this.professionTask,
      };
    },
    async paginate(requestedPage, citySeoName) {
      console.info("Paginate", requestedPage, citySeoName);
      this.isLoadingProfessionals = true;
      try {
        const response = await this.fetchProfessionals({
          professionTaskSeoName: this.professionTask.seoName,
          citySeoName,
          pageSize: this.pageSize,
          page: parseInt(requestedPage),
        });

        this.personList = response.people;
        this.paginationInfo = response.pagination;

        this.trackViewItemList();
      } catch (err) {
        this.$logger.error("Failed to paginate", err);
      }
      this.isLoadingProfessionals = false;
    },
    async fetchCity(seoName) {
      this.isLoadingCity = true;
      try {
        const { cityBySeoName } = await this.$query(CITY_BY_SEO_NAME, {
          seoName,
        });
        this.city = cityBySeoName;
        await this.$store.dispatch("listPage/setCity", cityBySeoName);
      } catch (e) {
        this.city = null;
        await this.$store.dispatch("listPage/setCity", null);
      } finally {
        this.isLoadingCity = false;
      }
    },
    priceGuideNavButtonClick() {
      this.$trackers.trackClick("PriceGuideNavigationButton");
      this.isPriceGuideNavButtonActive = true;
    },
    professionalsNavButtonClick() {
      this.$trackers.trackClick("ProfessionalsNavigationButton");
      this.isPriceGuideNavButtonActive = false;
    },
    onConsultingBlockClick() {
      this.$trackers.trackClick("PricesPageTopConsultingBlockButton");
      window.open("https://bit.ly/szaktanacstypeform", "_blank");
    },
  },
};
</script>
